// "Why this number" drawer — the anti-black-box interaction shared by every
// Trade screen. Any clickable value calls useExplainDrawer().open(payload) and a
// right-side slide-over derives the value from its inputs (formula + source
// feeds + version/timestamp + optional mini-chart + why-it-matters + deep link).
//
// Mount <ExplainProvider> once around the console; pages consume the hook.

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";

export interface ExplainSource {
  label: string;
  value: string;
  version?: string;
  ts?: string;
}

export interface ExplainPayload {
  title: string;
  valueDisplay?: string;
  formula: string;
  sources: ExplainSource[];
  chart?: ReactNode;
  whyItMatters: string;
  action?: { label: string; to: string };
  severity?: "info" | "warn" | "critical";
}

interface ExplainContextValue {
  open: (payload: ExplainPayload) => void;
  close: () => void;
}

const ExplainContext = createContext<ExplainContextValue | null>(null);

const SEVERITY_BAR: Record<NonNullable<ExplainPayload["severity"]>, string> = {
  info: "#60a5fa",
  warn: "#f59e0b",
  critical: "#f87171",
};

export function ExplainProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<ExplainPayload | null>(null);
  const open = useCallback((p: ExplainPayload) => setPayload(p), []);
  const close = useCallback(() => setPayload(null), []);
  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <ExplainContext.Provider value={value}>
      {children}
      <ExplainDrawer payload={payload} onClose={close} />
    </ExplainContext.Provider>
  );
}

export function useExplainDrawer(): ExplainContextValue {
  const ctx = useContext(ExplainContext);
  if (!ctx) throw new Error("useExplainDrawer must be used inside <ExplainProvider>");
  return ctx;
}

function ExplainDrawer({ payload, onClose }: { payload: ExplainPayload | null; onClose: () => void }) {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const accent = payload?.severity ? SEVERITY_BAR[payload.severity] : "#E4E4E7";

  return (
    <AnimatePresence>
      {payload && (
        <motion.div
          className="fixed inset-0 z-[90] flex justify-end"
          role="dialog"
          aria-modal="true"
          aria-label={`Explanation: ${payload.title}`}
          initial={{ opacity: reduce ? 1 : 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <button
            type="button"
            aria-label="Close explanation"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.aside
            className="relative h-full w-full max-w-[420px] overflow-y-auto border-l border-zinc-800 bg-[#0d0d0d] shadow-2xl"
            initial={reduce ? { x: 0 } : { x: "100%" }}
            animate={{ x: 0 }}
            exit={reduce ? { x: 0 } : { x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
          >
            <div className="h-1 w-full" style={{ backgroundColor: accent }} />
            <div className="flex items-start justify-between gap-3 border-b border-zinc-900 p-4">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500">Why this number</div>
                <h2 className="mt-0.5 truncate text-sm font-semibold text-zinc-100">{payload.title}</h2>
                {payload.valueDisplay && (
                  <div className="mt-1 text-2xl font-semibold" style={{ color: accent }}>
                    {payload.valueDisplay}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="rounded-md p-1.5 text-zinc-500 hover:bg-[#1a1a1a] hover:text-zinc-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-5 p-4">
              {payload.chart && (
                <div className="rounded-lg border border-zinc-900 bg-[#0f0f0f] p-3">{payload.chart}</div>
              )}

              <section>
                <h3 className="mb-1.5 text-[11px] uppercase tracking-wider text-zinc-500">How it&apos;s derived</h3>
                <p className="rounded-lg border border-zinc-900 bg-[#0a0a0a] p-3 font-code text-xs leading-relaxed text-zinc-300">
                  {payload.formula}
                </p>
              </section>

              {payload.sources.length > 0 && (
                <section>
                  <h3 className="mb-1.5 text-[11px] uppercase tracking-wider text-zinc-500">Sources</h3>
                  <dl className="space-y-1.5">
                    {payload.sources.map((s, i) => (
                      <div key={`${s.label}-${i}`} className="flex items-baseline justify-between gap-3 text-sm">
                        <dt className="shrink-0 text-zinc-500">{s.label}</dt>
                        <dd className="min-w-0 truncate text-right text-zinc-300">
                          {s.value}
                          {(s.version || s.ts) && (
                            <span className="ml-1 text-[11px] text-zinc-600">
                              {s.version ? `· ${s.version}` : ""}
                              {s.ts ? ` · ${s.ts}` : ""}
                            </span>
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              )}

              <section>
                <h3 className="mb-1.5 text-[11px] uppercase tracking-wider text-zinc-500">Why it matters</h3>
                <p className="text-sm leading-relaxed text-zinc-400">{payload.whyItMatters}</p>
              </section>

              {payload.action && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(payload.action!.to);
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#CCFF00] px-4 py-2.5 text-sm font-semibold text-black hover:bg-[#b8e600]"
                >
                  {payload.action.label}
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
